import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { formatDocument, partyDisplayName } from '@aeris/party'
import { Combobox } from '@aeris/ui'

import { fetchParties, fetchParty } from '../lib/api.ts'
import { useDebounced } from '../lib/use-debounced.ts'

/**
 * Pick a customer or a supplier by typing their name.
 *
 * Replaces the `<Select>` that three forms each grew independently, every one
 * of them fed `fetchParties({ pageSize: 100 })`. That is not a page size on a
 * picker — it is a silent ceiling: Elite Digital has 8988 parties, so the list
 * held the first hundred by name and the rest were unreachable with nothing on
 * screen admitting it. The purchase form made it worse by defaulting to
 * `items[0]`, so leaving the field untouched booked the purchase to whichever
 * supplier sorted first rather than to none.
 *
 * The `search` parameter has been on `/v1/parties` since F009. What was
 * missing was a control that used it.
 *
 * ## Why the chosen party is fetched separately
 *
 * When a form opens on an existing record it knows a `partyId` and nothing
 * else, and that party is almost never in the first page of an empty search.
 * Reading the label from the results list would leave the field blank on a
 * record that plainly has a customer — so the name is fetched by id, once, and
 * kept while the search list churns underneath it.
 */

export type PartyPickerProps = {
  value: string
  onChange: (partyId: string) => void
  /** Filters the search. Not a rule — the server still allows the other role. */
  role?: 'customer' | 'supplier' | 'carrier' | 'courier' | undefined
  id?: string
  'aria-describedby'?: string | undefined
  invalid?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
}

export const PartyPicker = ({
  value,
  onChange,
  role,
  invalid = false,
  disabled = false,
  placeholder = 'Buscar por nome ou documento',
  className,
  id,
  ...aria
}: PartyPickerProps): ReactNode => {
  const [search, setSearch] = useState('')
  const term = useDebounced(search)

  const results = useQuery({
    queryKey: ['party-picker', role ?? 'any', term],
    queryFn: ({ signal }) =>
      fetchParties(
        {
          ...(role === undefined ? {} : { role }),
          status: 'active',
          // Eight is what fits without scrolling. The search is the way to
          // reach the ninth, which is the whole point of the control.
          pageSize: 8,
          ...(term === '' ? {} : { search: term }),
        },
        signal
      ),
  })

  const chosen = useQuery({
    queryKey: ['party', value],
    queryFn: ({ signal }) => fetchParty(value, signal),
    enabled: value !== '',
  })

  /*
   * O código do sistema antigo vem primeiro.
   *
   * É por ele que a loja conhece as pessoas, e o documento quase sempre está em
   * branco — uma dica que só mostrava o CPF ficava vazia justamente nas listas
   * grandes, onde ela é necessária.
   */
  const items = (results.data?.items ?? []).map((party) => {
    const marks = [
      party.code === undefined || party.code === null
        ? party.externalCode === null
          ? null
          : `Cód. ${party.externalCode}`
        : `Cód. ${String(party.code)}`,
      party.document === null
        ? null
        : formatDocument(party.kind, party.document),
    ].filter((mark): mark is string => mark !== null)

    return {
      value: party.id,
      label: partyDisplayName(party),
      ...(marks.length === 0 ? {} : { hint: marks.join(' · ') }),
    }
  })

  // The chosen party's name, preferring whatever is currently on screen so a
  // fresh pick shows instantly rather than after a round trip.
  const selectedLabel =
    items.find((item) => item.value === value)?.label ??
    (chosen.data === undefined ? null : partyDisplayName(chosen.data.party))

  return (
    <Combobox
      {...(id === undefined ? {} : { id })}
      {...aria}
      value={value === '' ? null : value}
      onValueChange={(next) => {
        onChange(next ?? '')
      }}
      items={items}
      search={search}
      onSearchChange={setSearch}
      selectedLabel={selectedLabel}
      loading={results.isFetching}
      invalid={invalid}
      disabled={disabled}
      placeholder={placeholder}
      emptyMessage={
        term === ''
          ? 'Digite para buscar.'
          : `Ninguém encontrado para "${term}".`
      }
      clearable
      {...(className === undefined ? {} : { className })}
    />
  )
}
